<template>
  <transition
    id="dev-ManualTokenInputDialog"
    name="slide-up"
  >
    <div
      v-if="show"
      class="point-dialog manual-input-dialog"
      :class="{ 'keyboard-visible': keyboardVisible }"
      :style="dialogStyle"
    >
      <div class="point-dialog-content">
        <!-- Minimize Button -->
        <ModalCloseButton
          class="dialog-minimize-btn"
          @close="$emit('minimize')"
        />

        <!-- Header -->
        <div class="dialog-header manual-token-header">
          <div class="header-main">
            <h3 class="point-name">
              {{ $t('map_navigation.manual_token_title') }}
            </h3>
            <p class="point-address">
              {{ $t('map_navigation.manual_token_description') }}
            </p>
          </div>
        </div>

        <!-- QR Scanner -->
        <div
          v-if="qrCodeOn"
          class="qr-scanner-wrapper"
        >
          <div class="qr-scanner-container">
            <qrcode-stream
              @detect="onQrDecode"
              @init="onQrInit"
            >
              <div class="scanner-overlay">
                <div class="scanner-frame"></div>
              </div>
            </qrcode-stream>
          </div>
          <div class="text-center mt-3">
            <BaseButton
              variant="link"
              unstyled
              class="text-danger cancel-scan-btn"
              @click="qrCodeOn = false"
            >
              {{ $t('common.cancel') }}
            </BaseButton>
          </div>
        </div>

        <!-- Token Input Card -->
        <div v-else>
          <div class="compact-input-wrapper">
            <TokenInputCard
              :model-value="token"
              :error="error"
              :readonly="false"
              placeholder="####"
              @update:model-value="$emit('update:token', $event)"
            />
          </div>

          <!-- Scan Button (visible if no token entered) -->
          <div
            v-if="!token"
            class="scan-section-compact"
          >
            <div class="or-separator-compact">
              <span>{{ $t('login.or') }}</span>
            </div>

            <BaseButton
              bare
              variant=""
              class="btn-scan-qr-prominent"
              @click="qrCodeOn = true"
            >
              <font-awesome-icon
                icon="fa-solid fa-qrcode"
                class="me-2"
              />
              {{ $t('map_navigation.scan_qr_code') }}
            </BaseButton>
          </div>
        </div>

        <!-- Point Info (when token is valid) -->
        <div v-if="selectedPoint">
          <!-- Station Info -->
          <div
            class="dialog-header"
            style="margin-bottom: 16px"
          >
            <div class="header-main">
              <h3 class="point-name">
                {{ selectedPoint.name }}
              </h3>
              <div class="address-row-mobile">
                <a
                  :href="googleMapsUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="point-address point-address-link"
                >
                  <font-awesome-icon
                    icon="fa-solid fa-location-arrow"
                    class="address-icon"
                  />
                  {{ selectedPoint.address }}
                  <font-awesome-icon
                    icon="fa-solid fa-external-link-alt"
                    class="external-icon"
                  />
                </a>
                <!-- Distance Badge - Shown next to address on mobile -->
                <div
                  v-if="distanceFromUser"
                  class="distance-badge distance-badge-mobile"
                  :class="{ 'distance-far': isUserFarFromPoint }"
                >
                  <font-awesome-icon
                    icon="fa-solid fa-location-dot"
                    class="badge-icon"
                  />
                  <span class="distance-value">a {{ distanceFromUser }} km</span>
                </div>
              </div>
            </div>
            <!-- Distance Badge - Original position for desktop -->
            <div
              v-if="distanceFromUser"
              class="distance-badge distance-badge-desktop"
              :class="{ 'distance-far': isUserFarFromPoint }"
            >
              <font-awesome-icon
                icon="fa-solid fa-location-dot"
                class="badge-icon"
              />
              <span class="distance-value">a {{ distanceFromUser }} km</span>
            </div>
          </div>

          <!-- Availability Card -->
          <AvailabilityCard
            :available-batteries="selectedPoint.available_batteries"
            :available-slots="selectedPoint.available_slots"
          />
        </div>

        <!-- Action Buttons -->
        <div
          class="d-flex justify-content-center"
          style="margin-top: 20px"
        >
          <div
            v-if="selectedPoint && isOffline"
            class="no-battery-message offline-message"
          >
            <font-awesome-icon icon="fa-solid fa-plug-circle-xmark" />
            <span>{{ $t('map_navigation.terminal_offline') }}</span>
          </div>
          <BaseButton
            v-else-if="selectedPoint && hasAvailability"
            variant="primary"
            unstyled
            :disabled="!canSelect || isValidating || isLoadingLocal"
            class="select-station-btn"
            :aria-label="isReturnMode ? $t('map_navigation.return_here') : $t('map_navigation.select_station')"
            @click="handleSelectStation"
          >
            <span
              v-if="isValidating || isLoadingLocal"
              class="spinner-border spinner-border-sm me-2"
              role="status"
              aria-hidden="true"
            ></span>
            {{ isReturnMode ? $t('map_navigation.return_here') : $t('map_navigation.select_station') }}
          </BaseButton>
          <div
            v-else-if="selectedPoint"
            class="no-battery-message"
          >
            <font-awesome-icon icon="fa-solid fa-battery-empty" />
            <span>{{ noAvailabilityMessage }}</span>
          </div>
        </div>

        <!-- Check Prices Link -->
        <div
          v-if="selectedPoint"
          class="check-prices-container"
        >
          <BaseButton
            bare
            variant=""
            class="check-prices-link"
            @click="$emit('check-prices')"
          >
            {{ $t('map_navigation.check_prices') }}
          </BaseButton>
        </div>
      </div>

      <!-- Distance Confirmation Modal -->
      <DistanceConfirmationModal
        :show="showDistanceModal"
        :distance="distanceFromUser"
        :token="token"
        @confirm="proceedToCheckout"
        @cancel="showDistanceModal = false"
      />
    </div>
  </transition>
</template>

<script>
  import { QrcodeStream } from 'vue-qrcode-reader'
  import AvailabilityCard from '../ui/AvailabilityCard.vue'
  import TokenInputCard from '../ui/TokenInputCard.vue'
  import BaseButton from '@/components/ui/BaseButton.vue'
  import ModalCloseButton from '@/components/ui/ModalCloseButton.vue'
  import DistanceConfirmationModal from '../modals/DistanceConfirmationModal.vue'
  import { calculateDistance } from '../utils/mapHelpers'
  import { MAX_DISTANCE_KM } from '../utils/constants'

  export default {
    name: 'ManualTokenInputDialog',
    components: {
      QrcodeStream,
      AvailabilityCard,
      TokenInputCard,
      BaseButton,
      ModalCloseButton,
      DistanceConfirmationModal
    },
    props: {
      show: {
        type: Boolean,
        default: false
      },
      mode: {
        type: String,
        default: 'withdrawal'
      },
      token: {
        type: String,
        default: ''
      },
      selectedPoint: {
        type: Object,
        default: null
      },
      error: {
        type: String,
        default: ''
      },
      isValidating: {
        type: Boolean,
        default: false
      },
      userLocation: {
        type: Object,
        default: null
      },
      hasLocationPermission: {
        type: Boolean,
        default: false
      }
    },
    emits: ['minimize', 'update:token', 'select-station', 'check-prices'],
    data() {
      return {
        qrCodeOn: false,
        initialViewportHeight: window.innerHeight,
        keyboardVisible: false,
        keyboardBottomOffset: 0,
        visibleViewportHeight: window.innerHeight,
        showDistanceModal: false,
        isLoadingLocal: false
      }
    },
    computed: {
      isReturnMode() {
        return this.mode === 'return'
      },
      isOffline() {
        return this.selectedPoint && this.selectedPoint.online === false
      },
      distanceFromUser() {
        if (!this.selectedPoint || !this.userLocation || !this.hasLocationPermission) {
          return null
        }
        const distance = calculateDistance(
          this.userLocation.lat,
          this.userLocation.lng,
          parseFloat(this.selectedPoint.geolocation.lat),
          parseFloat(this.selectedPoint.geolocation.lng)
        )
        return distance.toFixed(1)
      },
      isUserFarFromPoint() {
        if (!this.distanceFromUser) {
          return false
        }
        return parseFloat(this.distanceFromUser) > MAX_DISTANCE_KM
      },
      googleMapsUrl() {
        if (!this.selectedPoint || !this.selectedPoint.geolocation) {
          return '#'
        }
        const lat = this.selectedPoint.geolocation.lat
        const lng = this.selectedPoint.geolocation.lng
        return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`
      },
      hasAvailability() {
        if (!this.selectedPoint || this.isOffline) return false
        const slots = parseInt(this.selectedPoint.available_slots || this.selectedPoint.avaible_slots || 0)
        const batteries = parseInt(this.selectedPoint.available_batteries || this.selectedPoint.avaible_batteries || 0)
        return this.isReturnMode ? slots > 0 : batteries > 0
      },
      canSelect() {
        return (
          this.selectedPoint && !this.isOffline && this.hasAvailability && this.token.trim().length === 4 && !this.error
        )
      },
      noAvailabilityMessage() {
        return this.isReturnMode ? this.$t('map_navigation.no_slots') : this.$t('map_navigation.no_batteries')
      },
      dialogStyle() {
        if (this.keyboardVisible) {
          return {
            bottom: `${this.keyboardBottomOffset}px`,
            maxHeight: `${this.visibleViewportHeight - 20}px`
          }
        }
        return {}
      }
    },
    watch: {
      show(newVal) {
        if (newVal) {
          this.isLoadingLocal = false
        }
      },
      isValidating(newVal) {
        if (newVal === false) {
          this.isLoadingLocal = false
        }
      }
    },
    mounted() {
      this.initialViewportHeight = window.innerHeight

      // Detectar mudanças no viewport (teclado virtual)
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', this.handleViewportChange)
      } else {
        // Fallback para navegadores que não suportam Visual Viewport API
        window.addEventListener('resize', this.handleViewportChange)
      }

      // Também detectar mudanças de orientação
      window.addEventListener('orientationchange', this.handleOrientationChange)
    },
    beforeUnmount() {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', this.handleViewportChange)
      } else {
        window.removeEventListener('resize', this.handleViewportChange)
      }
      window.removeEventListener('orientationchange', this.handleOrientationChange)
    },
    methods: {
      onQrDecode(results) {
        for (const detected of results) {
          if (detected && detected.rawValue) {
            let tempToken = detected.rawValue
            if (tempToken.includes('qr/')) {
              tempToken = tempToken.split('qr/')[1]
            }
            if (tempToken && tempToken.length === 4) {
              this.$emit('update:token', tempToken.toUpperCase())
              this.qrCodeOn = false
            }
          }
        }
      },
      onQrInit(promise) {
        promise.catch(error => {
          console.error('QR Scanner initialization error:', error)
        })
      },
      handleViewportChange() {
        const vv = window.visualViewport
        const currentWindowHeight = window.innerHeight
        const vvHeight = vv ? vv.height : currentWindowHeight
        const vvOffsetTop = vv ? vv.offsetTop : 0

        // True keyboard height: space between bottom of visual viewport and bottom of window.
        // On Android (resize mode) window.innerHeight already shrank, so keyboardHeight ≈ 0.
        // On iOS (overlay mode) window.innerHeight stays full, so keyboardHeight = actual keyboard.
        const keyboardHeight = Math.max(0, currentWindowHeight - vvOffsetTop - vvHeight)

        this.keyboardVisible = keyboardHeight > 150
        this.keyboardBottomOffset = keyboardHeight
        this.visibleViewportHeight = vvHeight
      },
      handleOrientationChange() {
        // Reset viewport height após mudança de orientação
        setTimeout(() => {
          this.initialViewportHeight = window.innerHeight
          this.visibleViewportHeight = window.innerHeight
          this.keyboardVisible = false
          this.keyboardBottomOffset = 0
        }, 300)
      },
      handleSelectStation() {
        if (this.isUserFarFromPoint) {
          this.showDistanceModal = true
        } else {
          this.proceedToCheckout()
        }
      },
      proceedToCheckout() {
        this.isLoadingLocal = true
        this.showDistanceModal = false
        this.$emit('select-station')
      },
      resetLoading() {
        this.isLoadingLocal = false
      }
    }
  }
</script>

<style scoped lang="scss">
  /* Estilos originais preservados */
  .slide-up-enter-active,
  .slide-up-leave-active {
    transition:
      transform 0.3s ease,
      opacity 0.3s ease;
  }

  .slide-up-enter-from,
  .slide-up-leave-to {
    transform: translateY(100%);
    opacity: 0;
  }

  .point-dialog {
    position: fixed;
    bottom: var(--app-fixed-bottom-offset, 0px);
    left: 0;
    right: 0;
    background: #fff;
    border-radius: 20px 20px 0 0;
    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
    z-index: 10000; /* Higher than SideBar (z-index: 1000) */
    max-height: calc(var(--app-viewport-safe-height, 100vh) - 106px);
    max-height: calc(var(--app-viewport-safe-height, 100dvh) - 106px);
    overflow-y: auto;
    transition:
      bottom 0.3s ease,
      max-height 0.3s ease;

    /* Pull handle indicator */
    &::before {
      content: '';
      width: 40px;
      height: 4px;
      background-color: #ddd;
      border-radius: 2px;
      position: absolute;
      top: 10px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 1;
    }
  }

  .manual-input-dialog {
    position: fixed;
    z-index: 10000; /* Match parent z-index */
  }

  .point-dialog-content {
    padding: 24px;
    padding-top: 30px; /* Extra padding for pull handle */
    position: relative;
  }

  .dialog-minimize-btn {
    position: absolute;
    top: 24px; /* Adjusted for pull handle */
    right: 16px;
    background: transparent;
    border: none;
    color: #6c757d;
    font-size: 20px;
    cursor: pointer;
    padding: 8px;
    transition: all 0.2s ease;
    border-radius: 50%;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;

    &:hover {
      background: #f8f9fa;
      color: #495057;
    }

    &:active {
      transform: scale(0.95);
    }
  }

  .dialog-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 16px;
    margin-bottom: 24px;
    padding-right: 40px;
  }

  .manual-token-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    margin-bottom: 20px;

    @media (max-width: 480px) {
      margin-bottom: 16px;
    }
  }

  .compact-input-wrapper {
    max-width: 280px;
    margin: 0 auto 24px auto;
    display: flex;
    justify-content: center;

    :deep(.token-card) {
      padding: 10px 16px;
      margin-bottom: 0;
      gap: 4px;
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    :deep(.token-card-label) {
      font-size: 10px;
      margin-bottom: 2px;
      text-align: center;
    }

    :deep(.token-input-field) {
      font-size: 16px;
      padding: 6px 10px;
      text-align: center;
    }
  }

  .or-separator-compact {
    display: flex;
    align-items: center;
    text-align: center;
    margin: 10px 0;
    color: #adb5bd;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;

    &::before,
    &::after {
      content: '';
      flex: 1;
      border-bottom: 1px solid #f1f3f5;
    }

    &::before {
      margin-right: 12px;
    }

    &::after {
      margin-left: 12px;
    }
  }

  .scan-section-compact {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
  }

  .btn-scan-qr-prominent {
    background: #f8faff;
    border: 1.5px solid #0066cc;
    color: #0066cc;
    padding: 10px 24px;
    border-radius: 10px;
    font-weight: var(--fw-semibold);
    font-size: var(--fs-sm);
    letter-spacing: -0.01em;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    width: auto;
    min-width: 180px;
    box-shadow: 0 2px 6px rgba(0, 102, 204, 0.1);

    &:hover {
      background: #0066cc;
      color: #fff;
      box-shadow: 0 4px 12px rgba(0, 102, 204, 0.2);
    }

    &:active {
      transform: scale(0.96);
    }
  }

  .cancel-scan-btn {
    text-decoration: none;
    font-weight: 500;
    font-size: 14px;
  }

  .qr-scanner-wrapper {
    margin-bottom: 20px;
  }

  .qr-scanner-container {
    width: 100%;
    aspect-ratio: 1;
    max-width: 300px;
    margin: 0 auto;
    border-radius: 12px;
    overflow: hidden;
    position: relative;
    background: #000;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  .scanner-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.3);
  }

  .scanner-frame {
    width: 70%;
    height: 70%;
    border: 2px solid #0066cc;
    border-radius: 12px;
    box-shadow: 0 0 0 100vmax rgba(0, 0, 0, 0.4);
    position: relative;

    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 2px;
      background: #0066cc;
      animation: scan-move 2s linear infinite;
    }
  }

  @keyframes scan-move {
    0% {
      top: 0;
    }
    100% {
      top: 100%;
    }
  }

  .header-main {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  }

  .address-row-mobile {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    width: 100%;
  }

  .point-name {
    font-size: 16px;
    font-weight: bold;
    color: #424242;
    margin: 0;
  }

  .point-address {
    font-size: 12px;
    color: #666;
    margin: 0;
    line-height: 1.5;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;
    min-width: 0;

    &.point-address-link {
      display: inline-flex;
      align-items: flex-start;
      gap: 5px;
      text-decoration: none;
      color: #1976d2;
      transition: all 0.25s ease;
      padding: 4px 0;
      border-radius: 6px;
      font-weight: 500;
      word-break: break-word;

      .address-icon {
        margin-top: 3px;
      }

      &:hover {
        color: #1565c0;
        background: rgba(25, 118, 210, 0.08);
        text-decoration: none;
        transform: translateX(2px);
      }

      &:active {
        transform: translateX(2px) scale(0.98);
      }

      .address-icon {
        font-size: 11px;
        color: #1976d2;
        opacity: 0.85;
      }

      .external-icon {
        font-size: 9px;
        opacity: 0.6;
        margin-left: 1px;
      }
    }
  }

  .distance-badge {
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 4px 10px;
    background: rgba(25, 118, 210, 0.08);
    border-radius: 16px;
    font-size: 11px;
    font-weight: 600;
    color: #1976d2;
    white-space: nowrap;
    flex-shrink: 0;
    border: 1px solid rgba(25, 118, 210, 0.15);

    &.distance-badge-mobile {
      display: none;
    }

    &.distance-far {
      background: rgba(255, 107, 53, 0.1);
      color: #f4511e;
      border: 1px solid rgba(255, 107, 53, 0.3);
      animation: pulse-warning 1.5s ease-in-out infinite;
    }
  }

  @keyframes pulse-warning {
    0%,
    100% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.8;
      transform: scale(1.05);
    }
  }

  .badge-icon {
    font-size: 12px;
  }

  .select-station-btn {
    padding: 10px 24px;
    background: #ff6600;
    color: #fff;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    margin: 0 auto;
    display: block;

    &:hover:not(:disabled) {
      background: #e55c00;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(255, 102, 0, 0.3);
    }

    &:active:not(:disabled) {
      transform: translateY(0);
    }

    &:disabled {
      background: #bdbdbd;
      cursor: not-allowed;
      opacity: 0.6;
    }
  }

  .no-battery-message {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 16px;
    background: #ffebee;
    color: #c62828;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    width: 100%;
  }

  .check-prices-container {
    text-align: center;
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid #e0e0e0;
  }

  .check-prices-link {
    padding: 4px 0;
    background: transparent;
    color: #5f82a5;
    border: none;
    border-bottom: 1px solid #0066cc;
    border-radius: 0;
    font-size: 11px;
    font-weight: 500;
    cursor: pointer;
    margin: 0;

    &:hover {
      color: #0052a3;
      border-bottom-color: #0052a3;
    }

    &:active {
      opacity: 0.8;
    }
  }

  @media (max-width: 768px) {
    .point-dialog-content {
      padding: 20px;
    }

    .dialog-header {
      flex-direction: row;
      gap: 8px;
      margin-bottom: 16px;
      align-items: flex-end;
    }

    .address-row-mobile {
      justify-content: flex-start;
    }

    .distance-badge-desktop {
      display: none !important;
    }

    .distance-badge-mobile {
      display: flex !important;
      margin-left: auto;
    }

    .point-name {
      font-size: 16px;
    }

    .point-address {
      font-size: 12px;
    }
  }

  @media (max-width: 480px) {
    .point-dialog {
      position: inherit;
      border-radius: 20px 20px 0 0;
    }

    .point-dialog-content {
      padding: 16px;
      padding-top: 26px; /* Adjusted for pull handle on mobile */
    }

    .dialog-minimize-btn {
      top: 20px; /* Adjusted for mobile */
    }

    .dialog-header {
      gap: 8px;
    }

    .point-name {
      font-size: 14px;
    }

    .point-address {
      font-size: 11px;
    }

    .select-station-btn {
      padding: 10px 16px;
      font-size: 13px;
    }

    .check-prices-link {
      font-size: 11px;
    }

    /* Quando teclado visível, o maxHeight é controlado via inline style (JS) */
  }
</style>
